import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import RecurrenceEditor, { defaultRecurrence, toRecurrencePayload } from './RecurrenceEditor';
import Icons from './Icons';

// Color/icon scheme for each event type - kept in sync with the volunteer-facing
// Calendar page so event types look and mean the same thing in both dashboards.
const EVENT_TYPE_STYLES = {
  meeting: { label: 'Meeting', bg: 'bg-primary-100', text: 'text-primary-700', border: 'border-primary-200', icon: Icons.calendar },
  volunteer_event: { label: 'Volunteer Event', bg: 'bg-success-100', text: 'text-success-700', border: 'border-success-200', icon: Icons.hand },
  training: { label: 'Training', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: Icons.graduationCap },
  fundraiser: { label: 'Fundraiser', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', icon: Icons.gift },
  other: { label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', icon: Icons.tag },
};
const getEventTypeStyle = (type) => EVENT_TYPE_STYLES[type] || EVENT_TYPE_STYLES.other;

const renderDescription = (description) => {
  if (!description) return null;
  return description.split('\n').map((line, i) => {
    const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
    if (urlMatch) {
      const parts = line.split(/(https?:\/\/[^\s]+)/);
      return (
        <span key={i}>
          {parts[0]}
          <a href={parts[1]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{parts[1]}</a>
          {parts[2]}{'\n'}
        </span>
      );
    }
    return <span key={i}>{line}{'\n'}</span>;
  });
};

const AdminCalendarManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('list'); // 'list', 'calendar', 'create', 'edit', or 'past'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [volunteers, setVolunteers] = useState([]);
  const [isOnline, setIsOnline] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const locationDebounceRef = useRef(null);
  const [assignedToAll, setAssignedToAll] = useState(true);
  const [assignedVolunteers, setAssignedVolunteers] = useState([]);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [recurrence, setRecurrence] = useState(defaultRecurrence(''));

  // Event form state
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    is_all_day: false,
    event_type: 'volunteer_event',
    video_link: '',
    meeting_id: '',
    meeting_passcode: '',
    agenda: '',
    post_event_notes: '',
    video_recording_url: '',
    post_event_summary: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch events when calendar month changes or when switching between past/upcoming
  useEffect(() => {
    if (view === 'calendar' || view === 'list') {
      fetchEvents();
    }
  }, [selectedDate, view, showPastEvents]);

  // Fetch volunteers when entering create or edit view
  useEffect(() => {
    if (view === 'create' || view === 'edit') {
      fetchVolunteers();
    }
  }, [view]);

  const fetchVolunteers = async () => {
    try {
      const response = await api.get('/calendar/volunteers');
      if (response.data.success) {
        setVolunteers(response.data.volunteers);
      }
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);

      // Build query params based on current view
      let params = {};

      if (view === 'list' && showPastEvents) {
        // Fetch past events
        params.upcoming = 'false';
      } else if (view === 'list' && !showPastEvents) {
        // Fetch upcoming events
        params.upcoming = 'true';
      } else if (view === 'calendar') {
        // For calendar view, fetch events for the selected month/year
        params.month = selectedDate.getMonth() + 1; // JavaScript months are 0-indexed
        params.year = selectedDate.getFullYear();
      }

      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/calendar/events?${queryString}` : '/calendar/events';

      const response = await api.get(url);

      if (response.data.success) {
        setEvents(response.data.events);
      } else {
        setError(response.data.message || 'Failed to load events');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();

    if (repeatEnabled && recurrence.freq === 'WEEKLY' && (!recurrence.byweekday || recurrence.byweekday.length === 0)) {
      setError('Select at least one day of the week for the recurring event');
      return;
    }
    if (repeatEnabled && recurrence.endMode === 'until' && !recurrence.until) {
      setError('Select an end date for the recurring event');
      return;
    }

    try {
      setSubmitting(true);

      const eventData = {
        ...eventForm,
        assigned_to_all: assignedToAll,
        assigned_volunteers: assignedToAll ? [] : assignedVolunteers,
        end_date: eventForm.end_date || eventForm.start_date,
        // Add video link to description if provided
        description: eventForm.video_link
          ? `${eventForm.description}\n\nJoin Meeting: ${eventForm.video_link}${eventForm.meeting_id ? `\nMeeting ID: ${eventForm.meeting_id}` : ''}${eventForm.meeting_passcode ? `\nPasscode: ${eventForm.meeting_passcode}` : ''}`
          : eventForm.description,
        recurrence: repeatEnabled ? toRecurrencePayload(recurrence) : undefined
      };

      const response = await api.post('/calendar/events', eventData);

      if (response.data.success) {
        resetForm();
        setView('list');
        fetchEvents();
      } else {
        setError(response.data.message || 'Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      setError('Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      const eventData = {
        ...eventForm,
        assigned_to_all: assignedToAll,
        assigned_volunteers: assignedToAll ? [] : assignedVolunteers,
        end_date: eventForm.end_date || eventForm.start_date,
        description: eventForm.video_link
          ? `${eventForm.description}\n\nJoin Meeting: ${eventForm.video_link}${eventForm.meeting_id ? `\nMeeting ID: ${eventForm.meeting_id}` : ''}${eventForm.meeting_passcode ? `\nPasscode: ${eventForm.meeting_passcode}` : ''}`
          : eventForm.description
      };

      const response = await api.put(`/calendar/events/${selectedEvent.id}`, eventData);

      if (response.data.success) {
        resetForm();
        setView('list');
        fetchEvents();
      } else {
        setError(response.data.message || 'Failed to update event');
      }
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/calendar/events/${eventId}`);

      if (response.data.success) {
        fetchEvents();
      } else {
        setError(response.data.message || 'Failed to delete event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete event');
    }
  };

  const handleViewRSVPs = async (event) => {
    try {
      // Fetch full event details including RSVPs
      const response = await api.get(`/calendar/events/${event.id}`);

      if (response.data.success) {
        // Include attendees and invitees in the event object
        setSelectedEvent({
          ...response.data.event,
          attendees: response.data.attendees,
          invitees: response.data.invitees || []
        });
        setView('view');
      } else {
        setError(response.data.message || 'Failed to load event details');
      }
    } catch (error) {
      console.error('Error loading event details:', error);
      setError('Failed to load event details');
    }
  };

  // Helper function to format date from database to input field
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    // Handle both ISO strings and Date objects
    const dateString = typeof dateStr === 'string' ? dateStr : dateStr.toISOString();
    // Extract just the date part (yyyy-MM-dd) from ISO string or timestamp
    return dateString.split('T')[0];
  };

  // Helper function to check if an event is in the past
  const isEventInPast = (event) => {
    if (!event) return false;
    const now = new Date();

    // Use end_date and end_time if available, otherwise use start_date and start_time
    const dateToCheck = event.end_date || event.start_date;
    const timeToCheck = event.end_time || event.start_time;

    // Parse event date in local timezone
    const dateParts = dateToCheck.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2]);

    // If event has a time, parse it and create date with time
    if (timeToCheck) {
      const [hours, minutes] = timeToCheck.split(':');
      const eventDateTime = new Date(year, month, day, parseInt(hours), parseInt(minutes), 0);
      return eventDateTime < now;
    } else {
      // If no time specified, consider event lasts all day (until end of day)
      const eventDateTime = new Date(year, month, day, 23, 59, 59, 999);
      return eventDateTime < now;
    }
  };

  const handleEditEvent = async (event) => {
    // Check if event is in the past
    if (isEventInPast(event)) {
      setError('Cannot edit past events. Use "Post Event" to add post-event information.');
      return;
    }

    try {
      // Fetch full event details
      const response = await api.get(`/calendar/events/${event.id}`);

      if (response.data.success) {
        setSelectedEvent(response.data.event);

        // Parse video link from description if it exists
        const description = response.data.event.description || '';
        const videoLinkMatch = description.match(/Join Meeting: (https?:\/\/[^\s\n]+)/);
        const meetingIdMatch = description.match(/Meeting ID: ([^\n]+)/);
        const passcodeMatch = description.match(/Passcode: ([^\s\n]+)/);

        // Clean description by removing video meeting info
        const cleanDescription = description
          .replace(/\n\nJoin Meeting: https?:\/\/[^\s\n]+/, '')
          .replace(/\nMeeting ID: [^\n]+/, '')
          .replace(/\nPasscode: [^\s\n]+/, '');

        setEventForm({
          title: response.data.event.title || '',
          description: cleanDescription,
          location: response.data.event.location || '',
          start_date: formatDateForInput(response.data.event.start_date),
          end_date: formatDateForInput(response.data.event.end_date),
          start_time: response.data.event.start_time || '',
          end_time: response.data.event.end_time || '',
          is_all_day: response.data.event.is_all_day || false,
          event_type: response.data.event.event_type || 'volunteer_event',
          video_link: videoLinkMatch ? videoLinkMatch[1] : '',
          meeting_id: meetingIdMatch ? meetingIdMatch[1] : '',
          meeting_passcode: passcodeMatch ? passcodeMatch[1] : '',
          agenda: response.data.event.agenda || '',
          post_event_notes: response.data.event.post_event_notes || '',
          video_recording_url: response.data.event.video_recording_url || '',
          post_event_summary: response.data.event.post_event_summary || ''
        });

        const eventAssignedToAll = response.data.event.assigned_to_all !== false;
        setAssignedToAll(eventAssignedToAll);
        setAssignedVolunteers(response.data.event.assigned_volunteers || []);
        setIsOnline((response.data.event.location || '').toLowerCase() === 'online');

        setView('edit');
      } else {
        setError(response.data.message || 'Failed to load event details');
      }
    } catch (error) {
      console.error('Error loading event details:', error);
      setError('Failed to load event details');
    }
  };

  const handlePostEvent = async (event) => {
    try {
      // Fetch full event details
      const response = await api.get(`/calendar/events/${event.id}`);

      if (response.data.success) {
        setSelectedEvent(response.data.event);

        setEventForm({
          title: response.data.event.title || '',
          description: response.data.event.description || '',
          location: response.data.event.location || '',
          start_date: formatDateForInput(response.data.event.start_date),
          end_date: formatDateForInput(response.data.event.end_date),
          start_time: response.data.event.start_time || '',
          end_time: response.data.event.end_time || '',
          is_all_day: response.data.event.is_all_day || false,
          event_type: response.data.event.event_type || 'volunteer_event',
          video_link: '',
          meeting_id: '',
          meeting_passcode: '',
          agenda: response.data.event.agenda || '',
          post_event_notes: response.data.event.post_event_notes || '',
          video_recording_url: response.data.event.video_recording_url || '',
          post_event_summary: response.data.event.post_event_summary || ''
        });

        setView('post-event');
      } else {
        setError(response.data.message || 'Failed to load event details');
      }
    } catch (error) {
      console.error('Error loading event details:', error);
      setError('Failed to load event details');
    }
  };

  const handleUpdatePostEvent = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      // Only send post-event fields
      const postEventData = {
        post_event_notes: eventForm.post_event_notes,
        video_recording_url: eventForm.video_recording_url,
        post_event_summary: eventForm.post_event_summary
      };

      const response = await api.put(`/calendar/events/${selectedEvent.id}`, postEventData);

      if (response.data.success) {
        resetForm();
        setView('list');
        fetchEvents();
      } else {
        setError(response.data.message || 'Failed to update post-event information');
      }
    } catch (error) {
      console.error('Error updating post-event information:', error);
      setError('Failed to update post-event information');
    } finally {
      setSubmitting(false);
    }
  };

  const searchAddress = async (query) => {
    if (query.length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5`,
        { headers: { 'Accept-Language': 'en-US,en' } }
      );
      const data = await res.json();
      setLocationSuggestions(data);
      setShowLocationSuggestions(data.length > 0);
    } catch {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    }
  };

  const formatAddressSuggestion = (place) => {
    const a = place.address || {};
    const parts = [];
    if (a.house_number && a.road) parts.push(`${a.house_number} ${a.road}`);
    else if (a.road) parts.push(a.road);
    const city = a.city || a.town || a.village || a.municipality;
    if (city) parts.push(city);
    if (a.state) parts.push(a.state);
    if (a.postcode) parts.push(a.postcode);
    return parts.join(', ') || place.display_name;
  };

  const resetForm = () => {
    setEventForm({
      title: '',
      description: '',
      location: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      is_all_day: false,
      event_type: 'volunteer_event',
      video_link: '',
      meeting_id: '',
      meeting_passcode: '',
      agenda: '',
      post_event_notes: '',
      video_recording_url: '',
      post_event_summary: ''
    });
    setAssignedToAll(true);
    setAssignedVolunteers([]);
    setSelectedEvent(null);
    setIsOnline(false);
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
    setRepeatEnabled(false);
    setRecurrence(defaultRecurrence(''));
  };

  const formatDate = (dateStr) => {
    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = dateStr.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Filter events based on past/upcoming
  const getFilteredEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for comparison

    return events.filter(event => {
      // Parse event date in local timezone to avoid timezone issues
      const dateParts = event.start_date.split('-');
      const eventDate = new Date(
        parseInt(dateParts[0]),
        parseInt(dateParts[1]) - 1,
        parseInt(dateParts[2]),
        0, 0, 0, 0
      );

      if (showPastEvents) {
        return eventDate < today; // Show past events
      } else {
        return eventDate >= today; // Show upcoming events (includes today)
      }
    }).sort((a, b) => {
      // Parse dates in local timezone for consistent sorting
      const datePartsA = a.start_date.split('-');
      const dateA = new Date(
        parseInt(datePartsA[0]),
        parseInt(datePartsA[1]) - 1,
        parseInt(datePartsA[2])
      );
      const datePartsB = b.start_date.split('-');
      const dateB = new Date(
        parseInt(datePartsB[0]),
        parseInt(datePartsB[1]) - 1,
        parseInt(datePartsB[2])
      );
      // For past events, show most recent first (descending)
      // For upcoming events, show soonest first (ascending)
      return showPastEvents ? dateB - dateA : dateA - dateB;
    });
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const matchingEvents = events.filter(event => {
      // Handle different date formats that might come from API
      let eventDate = event.start_date;
      if (eventDate) {
        // If it's a full datetime string, extract just the date part
        if (eventDate.includes('T')) {
          eventDate = eventDate.split('T')[0];
        }
        // If it's already a date string (YYYY-MM-DD), use it as is
        return eventDate === dateStr;
      }
      return false;
    });

    // Optional debug logging (remove in production)
    // if (date.getDate() === 1) {
    //   console.log('Calendar Debug - Events for month:', {
    //     totalEvents: events.length,
    //     sampleEvent: events[0],
    //     checkingDate: dateStr,
    //     matchingEvents: matchingEvents.length
    //   });
    // }

    return matchingEvents;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(selectedDate);
    const firstDay = getFirstDayOfMonth(selectedDate);
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Add day headers
    const dayHeaders = dayNames.map(day => (
      <div key={day} className="p-2 text-center text-sm font-semibold text-gray-700 bg-gray-50">
        {day}
      </div>
    ));

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="p-2 h-24 border border-gray-200 bg-gray-50"></div>
      );
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
      const dayEvents = getEventsForDate(currentDate);
      const isToday = currentDate.toDateString() === new Date().toDateString();

      // Optional debug logging for days with events (remove in production)
      // if (dayEvents.length > 0) {
      //   console.log(`Day ${day} has ${dayEvents.length} events:`, dayEvents.map(e => e.title));
      // }

      days.push(
        <div key={day} className={`p-2 h-24 border border-gray-200 bg-white hover:bg-gray-50 ${isToday ? 'bg-blue-50' : ''}`}>
          <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {dayEvents.length > 0 ? (
              <>
                {dayEvents.slice(0, 2).map(event => {
                  const style = getEventTypeStyle(event.event_type);
                  return (
                    <div
                      key={event.id}
                      onClick={() => handleViewRSVPs(event)}
                      className={`text-xs p-1 rounded border cursor-pointer truncate ${style.bg} ${style.text} ${style.border}`}
                      title={event.title}
                    >
                      {event.title}
                    </div>
                  );
                })}
                {dayEvents.length > 2 && (
                  <div className="text-xs text-gray-500">+{dayEvents.length - 2} more</div>
                )}
              </>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="grid grid-cols-7">
          {dayHeaders}
          {days}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Event Management</h2>
        <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={() => {
              setShowPastEvents(false);
              setView('list');
            }}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'list' && !showPastEvents
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Upcoming Events
          </button>
          <button
            onClick={() => setView('calendar')}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'calendar'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Calendar View
          </button>
          <button
            onClick={() => {
              setShowPastEvents(true);
              setView('list');
            }}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'list' && showPastEvents
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Past Meetings
          </button>
          <button
            onClick={() => {
              resetForm();
              setView('create');
            }}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'create'
                ? 'bg-success-500 text-white'
                : 'bg-success-500 text-white hover:bg-success-600'
            }`}
          >
            Create Event
          </button>
        </div>
      </div>

      {/* Events List View */}
      {view === 'list' && (
        <div className="bg-white rounded-lg shadow">
          {getFilteredEvents().length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {showPastEvents ? 'No past events' : 'No upcoming events'}
              </h3>
              <p className="text-gray-600 mb-4">
                {showPastEvents
                  ? 'No past events to display'
                  : 'Create your first event to get started'}
              </p>
              {!showPastEvents && (
                <button
                  onClick={() => setView('create')}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Create Event
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {getFilteredEvents().map((event) => (
                <div key={event.id} className="p-4 sm:p-6 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{event.title}</h3>
                        <span className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getEventTypeStyle(event.event_type).bg} ${getEventTypeStyle(event.event_type).text}`}>
                          {event.event_type.replace('_', ' ')}
                        </span>
                      </div>

                      <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="whitespace-nowrap">{formatDate(event.start_date)}</span>
                          {!event.is_all_day && event.start_time && (
                            <span className="whitespace-nowrap">{formatTime(event.start_time)}</span>
                          )}
                          {event.location && (
                            <span className="whitespace-nowrap">{event.location}</span>
                          )}
                        </div>

                        {event.description && event.description.includes('Join Meeting:') && (
                          <div className="text-blue-600">Video meeting included</div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-row sm:flex-col gap-2">
                      <button
                        onClick={() => handleViewRSVPs(event)}
                        className="flex-1 sm:flex-none bg-success-500 hover:bg-success-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
                      >
                        View/RSVPs
                      </button>
                      {isEventInPast(event) ? (
                        <button
                          onClick={() => handlePostEvent(event)}
                          className="flex-1 sm:flex-none bg-warning-500 hover:bg-warning-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          Post Event
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="flex-1 sm:flex-none bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="flex-1 sm:flex-none bg-danger-500 hover:bg-danger-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="space-y-6">
          {/* Calendar Navigation */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                {Icons.chevronLeft()}
              </button>

              <h3 className="text-xl font-semibold text-gray-900">
                {selectedDate.toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric'
                })}
              </h3>

              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                {Icons.chevronRight()}
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          {renderCalendarGrid()}

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSelectedDate(new Date())}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1 rounded text-sm font-medium transition-colors"
                >
                  Today
                </button>
                <span className="text-sm text-gray-600">
                  {events.length} events this month
                </span>
              </div>

              <button
                onClick={() => {
                  resetForm();
                  setView('create');
                }}
                className="bg-success-500 hover:bg-success-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Add Event
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="bg-white rounded-lg shadow p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Event Types</h4>
            <div className="flex flex-wrap gap-4">
              {Object.values(EVENT_TYPE_STYLES).map((style) => (
                <div key={style.label} className="flex items-center gap-1.5 text-sm text-gray-600">
                  {style.icon('w-4 h-4 text-gray-400')}
                  <span>{style.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Event Form */}
      {(view === 'create' || view === 'edit') && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              {view === 'create' ? 'Create New Event' : 'Edit Event'}
            </h3>
            <button
              type="submit"
              form="event-form"
              disabled={submitting}
              className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {submitting ? 'Saving...' : 'Save Event'}
            </button>
          </div>
          
          <form id="event-form" onSubmit={view === 'create' ? handleCreateEvent : handleUpdateEvent} className="space-y-6">
            {/* Basic Event Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  required
                  value={eventForm.title}
                  onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter event title..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Event description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type
                </label>
                <select
                  value={eventForm.event_type}
                  onChange={(e) => setEventForm({...eventForm, event_type: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="volunteer_event">Volunteer Event</option>
                  <option value="meeting">Meeting</option>
                  <option value="training">Training</option>
                  <option value="fundraiser">Fundraiser</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <label className="flex items-center space-x-2 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isOnline}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsOnline(checked);
                      setEventForm({...eventForm, location: checked ? 'Online' : ''});
                      setLocationSuggestions([]);
                      setShowLocationSuggestions(false);
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Online event (no physical location)</span>
                </label>
                {!isOnline ? (
                  <div className="relative">
                    <input
                      type="text"
                      value={eventForm.location}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEventForm({...eventForm, location: val});
                        if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
                        locationDebounceRef.current = setTimeout(() => searchAddress(val), 350);
                      }}
                      onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 150)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Start typing an address..."
                      autoComplete="off"
                    />
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {locationSuggestions.map((place) => (
                          <li
                            key={place.place_id}
                            onMouseDown={() => {
                              const formatted = formatAddressSuggestion(place);
                              setEventForm({...eventForm, location: formatted});
                              setShowLocationSuggestions(false);
                              setLocationSuggestions([]);
                            }}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                          >
                            {formatAddressSuggestion(place)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div className="w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-50 text-gray-500 text-sm">
                    Online
                  </div>
                )}
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({...eventForm, start_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank if same as start date</p>
              </div>

              <div>
                <label className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    checked={eventForm.is_all_day}
                    onChange={(e) => setEventForm({...eventForm, is_all_day: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">All Day Event</span>
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Assign Volunteers
                </label>
                <div className="border border-gray-200 rounded-md p-4 space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assignedToAll}
                      onChange={(e) => {
                        setAssignedToAll(e.target.checked);
                        if (e.target.checked) setAssignedVolunteers([]);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">All Volunteers</span>
                  </label>
                  {!assignedToAll && (
                    <div className="ml-6 space-y-2 max-h-48 overflow-y-auto">
                      {volunteers.length === 0 ? (
                        <p className="text-sm text-gray-500">No volunteers found.</p>
                      ) : (
                        volunteers.map((v) => {
                          const displayName = (v.first_name || v.last_name)
                            ? `${v.first_name || ''} ${v.last_name || ''}`.trim()
                            : v.email;
                          const isChecked = assignedVolunteers.includes(v.email);
                          return (
                            <label key={v.email} className="flex items-center space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAssignedVolunteers([...assignedVolunteers, v.email]);
                                  } else {
                                    setAssignedVolunteers(assignedVolunteers.filter(email => email !== v.email));
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">
                                {displayName}
                                <span className="text-gray-400 ml-1">({v.email})</span>
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!eventForm.is_all_day && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={eventForm.start_time}
                      onChange={(e) => setEventForm({...eventForm, start_time: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={eventForm.end_time}
                      onChange={(e) => setEventForm({...eventForm, end_time: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Recurrence (create only - editing a recurring series isn't supported yet) */}
            {view === 'create' && (
              <div className="border-t pt-6">
                <label className="flex items-center space-x-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={repeatEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setRepeatEnabled(checked);
                      if (checked) setRecurrence(defaultRecurrence(eventForm.start_date));
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Repeat this event</span>
                </label>

                {repeatEnabled && (
                  <RecurrenceEditor
                    recurrence={recurrence}
                    onChange={setRecurrence}
                    startDate={eventForm.start_date}
                  />
                )}
              </div>
            )}

            {/* Video Meeting Section */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Video Meeting (Optional)</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Link
                  </label>
                  <input
                    type="url"
                    value={eventForm.video_link}
                    onChange={(e) => setEventForm({...eventForm, video_link: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://zoom.us/j/123456789 or https://meet.google.com/abc-def-ghi"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={eventForm.meeting_id}
                    onChange={(e) => setEventForm({...eventForm, meeting_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="123-456-789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passcode (Optional)
                  </label>
                  <input
                    type="text"
                    value={eventForm.meeting_passcode}
                    onChange={(e) => setEventForm({...eventForm, meeting_passcode: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Meeting passcode"
                  />
                </div>
              </div>
            </div>

            {/* Meeting Agenda Section */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-2">Meeting Agenda (Optional)</h4>
              <p className="text-sm text-gray-500 mb-4">Add an agenda for your event. You can also add this after creating the event.</p>

              <div>
                <textarea
                  value={eventForm.agenda}
                  onChange={(e) => setEventForm({...eventForm, agenda: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="4"
                  placeholder="Enter meeting agenda here...&#10;&#10;Example:&#10;1. Welcome and introductions&#10;2. Review of last meeting&#10;3. Discussion topics&#10;4. Next steps"
                />
              </div>
            </div>

            {/* Post-Event Section - Only show when editing */}
            {view === 'edit' && selectedEvent && (
              <div className="border-t pt-6 bg-yellow-50 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Post-Event Information</h4>
                <p className="text-sm text-gray-600 mb-4">Add notes, video recording, and summary after the event has occurred.</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Post-Event Notes
                    </label>
                    <textarea
                      value={eventForm.post_event_notes}
                      onChange={(e) => setEventForm({...eventForm, post_event_notes: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                      rows="3"
                      placeholder="Key takeaways, action items, attendance notes, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video Recording URL
                    </label>
                    <input
                      type="url"
                      value={eventForm.video_recording_url}
                      onChange={(e) => setEventForm({...eventForm, video_recording_url: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                      placeholder="https://drive.google.com/... or https://youtube.com/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Summary
                    </label>
                    <textarea
                      value={eventForm.post_event_summary}
                      onChange={(e) => setEventForm({...eventForm, post_event_summary: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                      rows="4"
                      placeholder="Overall summary of what happened during the event, outcomes, and next steps..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setView('list');
                }}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {submitting ? 'Saving...' : 'Save Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* View Event RSVPs */}
      {view === 'view' && selectedEvent && (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{selectedEvent.title}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {new Date(selectedEvent.start_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                {!selectedEvent.is_all_day && selectedEvent.start_time && (
                  <span> at {new Date(`2000-01-01T${selectedEvent.start_time}`).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}</span>
                )}
              </p>
            </div>
            <button
              onClick={() => {
                setView('list');
                setSelectedEvent(null);
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
          </div>

          {/* Event Details */}
          <div className="mb-6 space-y-3">
            {selectedEvent.location && (
              <div className="flex items-start">
                <span className="text-gray-600 w-24 flex-shrink-0">Location:</span>
                <span className="text-gray-900">{selectedEvent.location}</span>
              </div>
            )}
            {selectedEvent.description && (
              <div className="flex items-start">
                <span className="text-gray-600 w-24 flex-shrink-0">Details:</span>
                <span className="text-gray-900 whitespace-pre-wrap">{renderDescription(selectedEvent.description)}</span>
              </div>
            )}
            {selectedEvent.event_type && (
              <div className="flex items-start">
                <span className="text-gray-600 w-24 flex-shrink-0">Type:</span>
                <span className="text-gray-900 capitalize">{selectedEvent.event_type.replace('_', ' ')}</span>
              </div>
            )}
          </div>

          {/* Meeting Agenda */}
          {selectedEvent.agenda && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-2 flex items-center">
                Meeting Agenda
              </h4>
              <div className="text-gray-900 whitespace-pre-wrap">{selectedEvent.agenda}</div>
            </div>
          )}

          {/* Post-Event Information */}
          {(selectedEvent.post_event_notes || selectedEvent.video_recording_url || selectedEvent.post_event_summary) && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-3">
                Post-Event Information
              </h4>
              <div className="space-y-3">
                {selectedEvent.post_event_notes && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Notes:</span>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap">{selectedEvent.post_event_notes}</p>
                  </div>
                )}
                {selectedEvent.video_recording_url && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Video Recording:</span>
                    <div className="mt-1">
                      <a
                        href={selectedEvent.video_recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                      >
                        {selectedEvent.video_recording_url}
                      </a>
                    </div>
                  </div>
                )}
                {selectedEvent.post_event_summary && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Summary:</span>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap">{selectedEvent.post_event_summary}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invitees List */}
          {selectedEvent.invitees && selectedEvent.invitees.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Invitees ({selectedEvent.invitees.length})
              </h4>
              <div className="space-y-2">
                {selectedEvent.invitees.map((invitee, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <div className="font-medium text-gray-900">
                        {invitee.first_name ? `${invitee.first_name} ${invitee.last_name}` : invitee.user_email}
                      </div>
                      {invitee.first_name && (
                        <div className="text-sm text-gray-500">{invitee.user_email}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {invitee.rsvp_status ? (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          invitee.rsvp_status === 'signed_up'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {invitee.rsvp_status === 'signed_up' ? 'Attending' : 'Cancelled'}
                        </span>
                      ) : (
                        <div
                          className="flex items-center gap-1.5"
                          title={invitee.notification_sent
                            ? `Notified ${new Date(invitee.notification_sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                            : 'Notification not yet sent'}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${invitee.notification_sent ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className={`text-xs ${invitee.notification_sent ? 'text-gray-500' : 'text-gray-600'}`}>
                            {invitee.notification_sent ? 'Notified' : 'Pending'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={() => {
                setView('list');
                setSelectedEvent(null);
              }}
              className="bg-gray-600 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Back to List
            </button>
            {isEventInPast(selectedEvent) ? (
              <button
                onClick={() => handlePostEvent(selectedEvent)}
                className="bg-warning-500 hover:bg-warning-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Add Post-Event Info
              </button>
            ) : (
              <button
                onClick={() => handleEditEvent(selectedEvent)}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Edit Event
              </button>
            )}
          </div>
        </div>
      )}

      {/* Post-Event Form */}
      {view === 'post-event' && selectedEvent && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            Add Post-Event Information
          </h3>

          {/* Event Info (Read-Only) */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">{selectedEvent.title}</h4>
            <p className="text-sm text-gray-600">
              {formatDate(selectedEvent.start_date)}
              {!selectedEvent.is_all_day && selectedEvent.start_time && (
                <span> at {formatTime(selectedEvent.start_time)}</span>
              )}
            </p>
            {selectedEvent.location && (
              <p className="text-sm text-gray-600 mt-1">{selectedEvent.location}</p>
            )}
          </div>

          <form onSubmit={handleUpdatePostEvent} className="space-y-6">
            {/* Post-Event Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Post-Event Notes
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Key takeaways, action items, attendance notes, etc.
              </p>
              <textarea
                value={eventForm.post_event_notes}
                onChange={(e) => setEventForm({...eventForm, post_event_notes: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="4"
                placeholder="Enter post-event notes here..."
              />
            </div>

            {/* Video Recording URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video Recording URL
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Link to the recording of this event (YouTube, Google Drive, etc.)
              </p>
              <input
                type="url"
                value={eventForm.video_recording_url}
                onChange={(e) => setEventForm({...eventForm, video_recording_url: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="https://drive.google.com/... or https://youtube.com/..."
              />
            </div>

            {/* Event Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Summary
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Overall summary of what happened during the event, outcomes, and next steps
              </p>
              <textarea
                value={eventForm.post_event_summary}
                onChange={(e) => setEventForm({...eventForm, post_event_summary: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="5"
                placeholder="Summarize the event outcomes, key discussions, and next steps..."
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setView('list');
                }}
                className="bg-gray-600 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-warning-500 hover:bg-warning-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save Post-Event Info'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminCalendarManager;